export const HINT_TOOLTIP_WIDTH = 240;
export const HINT_TOOLTIP_VIEWPORT_MARGIN = 16;
const HINT_TOOLTIP_PIN_WIDTH = 24;
/** Pin (8) + typical padded bubble — used to decide top vs bottom before measure. */
export const HINT_TOOLTIP_ESTIMATED_HEIGHT = 120;

export type HintTooltipAlign = "left" | "right";

type HintTooltipSide = "top" | "bottom";

export type HintTooltipPlacement = {
  /** Distance from viewport top when the bubble opens below the trigger. */
  top?: number;
  /** Distance from viewport bottom when the bubble opens above the trigger. */
  bottom?: number;
  left: number;
  pinOffset: number;
  width: number;
  side: HintTooltipSide;
};

/**
 * Places the 240px hint bubble above the trigger by default (design-system
 * standard), flipping below when there is not enough room. Then slides it
 * horizontally so it stays inside the viewport. `align` is only the preference
 * — iPad and phone both clip `position:absolute` bubbles via the app shell's
 * overflow-x-clip, so callers cannot rely on left/right classes alone.
 */
export const getHintTooltipPlacement = ({
  trigger,
  viewportWidth,
  viewportHeight,
  align,
  estimatedHeight = HINT_TOOLTIP_ESTIMATED_HEIGHT,
}: {
  trigger: Pick<DOMRect, "left" | "right" | "top" | "bottom">;
  viewportWidth: number;
  viewportHeight: number;
  align: HintTooltipAlign;
  estimatedHeight?: number;
}): HintTooltipPlacement => {
  const width = Math.min(
    HINT_TOOLTIP_WIDTH,
    Math.max(0, viewportWidth - HINT_TOOLTIP_VIEWPORT_MARGIN * 2),
  );
  const minLeft = HINT_TOOLTIP_VIEWPORT_MARGIN;
  const maxLeft = viewportWidth - width - HINT_TOOLTIP_VIEWPORT_MARGIN;
  const preferredLeft =
    align === "right" ? trigger.right - width : trigger.left;
  const left =
    maxLeft < minLeft
      ? minLeft
      : Math.min(Math.max(preferredLeft, minLeft), maxLeft);

  const pinAnchor =
    align === "right"
      ? trigger.right - HINT_TOOLTIP_PIN_WIDTH - 8
      : trigger.left + 8;
  const maxPinOffset = Math.max(8, width - HINT_TOOLTIP_PIN_WIDTH - 8);
  const pinOffset = Math.min(Math.max(pinAnchor - left, 8), maxPinOffset);

  const spaceAbove = trigger.top - HINT_TOOLTIP_VIEWPORT_MARGIN;
  const spaceBelow =
    viewportHeight - trigger.bottom - HINT_TOOLTIP_VIEWPORT_MARGIN;
  const side: HintTooltipSide =
    spaceAbove >= estimatedHeight || spaceAbove >= spaceBelow
      ? "top"
      : "bottom";

  if (side === "top") {
    return {
      bottom: viewportHeight - trigger.top,
      left,
      pinOffset,
      width,
      side,
    };
  }

  return { top: trigger.bottom, left, pinOffset, width, side };
};
